import React from 'react';
import { useAuthInfo } from '@propelauth/react';
import { includes, isEmpty } from 'lodash';
import UnavailabilityBox from './calendar-items/UnavailabilityBox';
import OrderBox from './calendar-items/OrderBox';
import HoursBorders from './views/components/HoursBorders';
import EventBox from './calendar-items/EventBox';
import { getCalendarCellLocations } from './calendar-items/CalendarCalculator';
import { CalendarItems } from './calendarWrapper/CalendarWrapper';
import { AccountData, EnrichedEventData, OrderData } from '../api';
import { unifySlot } from './UnifiedShiftAndUserAvailability';
import { UnifiedShiftsUserAvailabilitiesBox } from './calendar-items/UnifiedShiftsUserAvailabilitiesBox';
import { ResourcesBox } from './calendar-items/ResourcesBox';
import { useAuth } from '../session/InternalAuthProvider';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { useCalendar } from './CalendarContext/CalendarContext';
import DragDropProvider from './views/dragndrop/DragDropProvider';
import NoteBox from './calendar-items/NoteBox';
import { CalendarLocalPreferences } from './views/useCalendarLocalPreferences';
import { PrivateSlotAvailabilityBox } from './calendar-items/PrivateSlotAvailabilityBox';

type CalendarDayProps = {
  dateToRender: string;
  items: CalendarItems;
  openOrder: (event: any, order: OrderData) => void;
  openEvent: (event: EnrichedEventData) => void;
  preferences: CalendarLocalPreferences;
  numOfDays: number;
};
const CalendarDay = ({
  items,
  preferences: { shouldShowUnavailability, shouldShowOnlyCurrentUser, shouldShowShifts, shouldShowUserAvailabilities },
  dateToRender,
  openOrder,
  openEvent,
  numOfDays,
}: CalendarDayProps) => {
  const { authState } = useAuth();

  const account = authState.account as unknown as AccountData;
  const { user } = useAuthInfo();
  const { readOnly } = UseAuthPermissions();
  const { selectedUsersIds } = useCalendar();

  if (!items) {
    return <HoursBorders />;
  }

  // Check if account has scheduler configs for resources view
  const shouldShowResources =
    account?.shared_resources?.scheduler_configs?.length &&
    account?.shared_resources?.scheduler_configs?.length > 0 &&
    numOfDays === 1;
  const unavailabilitiesByDate = items.unavailabilities[dateToRender] || [];
  const notesByDate = items.notes[dateToRender] || [];
  const filteredNotes = notesByDate.filter((x) => !x.all_day);
  const filteredUnavailabilities = shouldShowUnavailability ? unavailabilitiesByDate?.filter((u) => !u.all_day) : [];
  const eventsByDate = items.events[dateToRender] || [];
  const filteredEvents = shouldShowOnlyCurrentUser
    ? eventsByDate?.filter((x) => x.employee_ids.includes(user?.userId || ''))
    : eventsByDate;
  const ordersByDate = items.orders[dateToRender] || [];
  const filteredOrders = shouldShowOnlyCurrentUser
    ? ordersByDate?.filter((x) => x.employee_ids.includes(user?.userId || ''))
    : ordersByDate;
  const filteredShifts = shouldShowShifts
    ? items.shifts[dateToRender]?.filter((x) =>
        isEmpty(selectedUsersIds) ? x : includes(selectedUsersIds, x.employee_id),
      ) || []
    : [];
  const userAvailabilities =
    shouldShowUserAvailabilities && (!account.should_hide_user_availability_from_employees || !readOnly)
      ? items.calendarUserAvailabilities[dateToRender]?.filter((x) =>
          isEmpty(selectedUsersIds) ? x : includes(selectedUsersIds, x.user_id),
        ) || []
      : [];

  const filteredPrivateSlotAvailabilities = shouldShowUnavailability
    ? items.privateSlotAvailabilities[dateToRender] || []
    : [];

  const { unifiedSlot, filteredOrdersLeft, filteredEventsLeft } = unifySlot(
    filteredShifts,
    userAvailabilities,
    filteredOrders,
    filteredEvents,
  );

  const cssResult = getCalendarCellLocations(
    filteredOrdersLeft,
    filteredEventsLeft,
    filteredUnavailabilities,
    unifiedSlot,
    filteredNotes,
    filteredPrivateSlotAvailabilities,
  );
  const shouldNotDragNDrop = readOnly || (!shouldShowUserAvailabilities && !shouldShowShifts);

  if (shouldShowResources) {
    return (
      <DragDropProvider orders={filteredOrders} shiftAndAvailabilities={unifiedSlot} events={filteredEvents}>
        <HoursBorders />
        <ResourcesBox
          orders={filteredOrders}
          unavailabilities={filteredUnavailabilities}
          openOrder={openOrder}
          css={{
            width: 100,
            left: 0,
            top: 0,
            height: 600, // TODO: Calculate properly
          }}
        />
      </DragDropProvider>
    );
  }

  return (
    <DragDropProvider orders={filteredOrders} shiftAndAvailabilities={unifiedSlot} events={filteredEvents}>
      <HoursBorders />
      {filteredOrdersLeft.map((order) => {
        const css = cssResult.find((x) => x.itemId === order.id);
        return (
          <OrderBox
            viewType="day"
            key={order.id}
            order={order}
            css={css}
            openOrder={openOrder}
            shouldNotDragNDrop={shouldNotDragNDrop}
          />
        );
      })}
      {filteredEventsLeft.map((event) => {
        const css = cssResult.find((x) => x.itemId === event.id);
        return (
          <EventBox
            viewType="day"
            key={event.id}
            event={event}
            css={css}
            openEvent={openEvent}
            shouldNotDragNDrop={shouldNotDragNDrop}
          />
        );
      })}
      {filteredUnavailabilities.map((unavailability) => {
        const css = cssResult.find((x) => x.itemId === unavailability.id);
        return <UnavailabilityBox viewType="day" key={unavailability.id} unavailability={unavailability} css={css} />;
      })}
      {filteredNotes.map((note) => {
        const css = cssResult.find((x) => x.itemId === note.id);
        return <NoteBox viewType="day" key={note.id} note={note} css={css} />;
      })}
      {unifiedSlot.map((shiftAndAvailability) => {
        const css = cssResult.find((x) => x.itemId === shiftAndAvailability.id);
        return (
          <UnifiedShiftsUserAvailabilitiesBox
            viewType="day"
            key={shiftAndAvailability.id}
            shiftsAndUserAvailability={shiftAndAvailability}
            css={css}
            date={dateToRender}
            openOrder={openOrder}
            openEvent={openEvent}
          />
        );
      })}
      {filteredPrivateSlotAvailabilities.map((privateSlotAvailability) => {
        const css = cssResult.find((x) => x.itemId === privateSlotAvailability.id);
        return (
          <PrivateSlotAvailabilityBox
            viewType="day"
            key={privateSlotAvailability.id}
            privateSlotAvailability={privateSlotAvailability}
            css={css}
          />
        );
      })}
    </DragDropProvider>
  );
};

export default CalendarDay;
